import { boundingBox, type Point } from '@spotnote/shared/geometry'
import { h } from './dom'
import { resolveTargets, samplePolyline, sampleRect } from './target'
import type { ComposerTarget, Store } from './state'

/** A rect smaller than this in either axis was almost certainly a stray click. */
const MIN_RECT = 14
/** Manhattan distance a pointer must travel before a sketch records a point. */
const SAMPLE_STEP = 4
/** Fewer points than this is a click, not a stroke. */
const MIN_STROKE_POINTS = 6

/** Viewport coordinates → document coordinates. */
function toDocument(event: PointerEvent): Point {
  return [
    Math.max(0, Math.round(event.clientX + window.scrollX)),
    Math.max(0, Math.round(event.clientY + window.scrollY)),
  ]
}

/**
 * The full-viewport surface that intercepts pointer input while a tool is
 * armed. It sits above the host page so a click annotates rather than
 * navigates, but it does not capture wheel events, so the client can still
 * scroll down and keep annotating.
 */
export function createCaptureSurface(store: Store, openComposer: (t: ComposerTarget) => void) {
  const surface = h('div', { class: 'capture' })

  // The pin's target is resolved at pointerdown, before anything on the page
  // has had a chance to move — but the composer opens at pointerup like the
  // other tools: opened mid-press, the click's own default focus handling
  // immediately steals the caret from the freshly focused textarea.
  let pendingPin: ComposerTarget | null = null

  surface.addEventListener('pointerdown', (event) => {
    const pointer = event as PointerEvent
    if (pointer.button !== 0) return
    const state = store.get()
    if (!state.tool) return

    surface.setPointerCapture(pointer.pointerId)
    const [x, y] = toDocument(pointer)

    if (state.tool === 'pin') {
      const targets = resolveTargets([[pointer.clientX, pointer.clientY]])
      pendingPin = { type: 'pin', x, y, anchorX: x, anchorY: y, targets }
      return
    }
    if (state.tool === 'rect') {
      store.set({ draft: { kind: 'rect', x, y, x2: x, y2: y } })
      return
    }
    store.set({ draft: { kind: 'draw', points: [[x, y]] } })
  })

  surface.addEventListener('pointermove', (event) => {
    const draft = store.get().draft
    if (!draft) return

    const [x, y] = toDocument(event as PointerEvent)

    if (draft.kind === 'rect') {
      store.set({ draft: { ...draft, x2: x, y2: y } })
      return
    }

    const last = draft.points[draft.points.length - 1]
    if (!last) return
    if (Math.abs(x - last[0]) + Math.abs(y - last[1]) > SAMPLE_STEP) {
      store.set({ draft: { kind: 'draw', points: [...draft.points, [x, y]] } })
    }
  })

  const finish = () => {
    if (pendingPin) {
      const pin = pendingPin
      pendingPin = null
      openComposer(pin)
      return
    }

    const draft = store.get().draft
    if (!draft) return

    if (draft.kind === 'rect') {
      const x = Math.min(draft.x, draft.x2)
      const y = Math.min(draft.y, draft.y2)
      const w = Math.abs(draft.x2 - draft.x)
      const hgt = Math.abs(draft.y2 - draft.y)

      if (w > MIN_RECT && hgt > MIN_RECT) {
        // Sampled across the box so a highlight round a price block reports the
        // block *and* the prices, not only whatever sits dead centre.
        const targets = resolveTargets(
          sampleRect(x - window.scrollX, y - window.scrollY, w, hgt),
        )
        openComposer({
          type: 'rect',
          x,
          y,
          w,
          h: hgt,
          anchorX: x + w / 2,
          anchorY: y + hgt,
          targets,
        })
      } else {
        store.set({ draft: null })
      }
      return
    }

    if (draft.points.length > MIN_STROKE_POINTS) {
      const box = boundingBox(draft.points)
      const targets = resolveTargets(
        samplePolyline(draft.points).map(
          ([px, py]) => [px - window.scrollX, py - window.scrollY] as [number, number],
        ),
      )
      openComposer({
        type: 'draw',
        points: draft.points,
        anchorX: (box.minX + box.maxX) / 2,
        anchorY: box.maxY,
        targets,
      })
    } else {
      store.set({ draft: null })
    }
  }

  surface.addEventListener('pointerup', finish)
  surface.addEventListener('pointercancel', () => {
    pendingPin = null
    store.set({ draft: null })
  })

  return surface
}
